import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  NextQuestion,
  Question,
  StartAiSystemRequest,
  StartAiSystemResponse
} from '../../types/AISystems.types';
import { Entity } from '../../types/Shared.types';

export interface StepperState {
  steps: Step[];
  showPreStep: boolean;
  preStepPart: PreStepPart;
  disableNextStep: boolean;
  selectedEntity: Entity | null;
  startAiSystemBodyRequest: StartAiSystemRequest;
  startAiSystemBodyResponse: StartAiSystemResponse | null;
  nextQuestion: NextQuestion | null;
  previousQuestions: Question[];
  questionnaireId: string;
  aiSystemId: string;
  providerDeclarationChecked: boolean;
  isStepBack: boolean;
  loading: boolean;
  isChangingAssessment: boolean;
  isFinalizingAIReport: boolean;
  answerRequestsInFlight: number;
  creatingAiSystem: boolean;
  stepType: string;
  enableStepper: boolean;
  showEmptyState: boolean;
  isDraftLoaded: boolean;
  preStepInputError: PreStepInputError;
  serverErrorStatus: number | null;
}

type Step = {
  id: string;
  number: number;
  title: string;
  status: 'complete' | 'current' | 'upcoming';
};

type PreStepInputError = {
  name?: string;
  version?: string;
  description?: string;
  organization?: string;
  compliance?: string;
  entity?: string;
} | null;

type PreStepPart = 'basicInformation' | 'entityClassification';

const initialState: StepperState = {
  steps: [
    {
      id: 'STEP1',
      number: 1,
      title: 'Changes & Integration',
      status: 'current'
    },
    { id: 'STEP2', number: 2, title: 'Risk Assessment', status: 'upcoming' },
    {
      id: 'STEP3',
      number: 3,
      title: 'Scope & Restrictions',
      status: 'upcoming'
    },
    {
      id: 'REPORT',
      number: 4,
      title: 'Compliance Results',
      status: 'upcoming'
    }
  ],
  startAiSystemBodyRequest: {
    name: '',
    version: '',
    description: '',
    organization: '',
    complianceContact: '',
    assessmentType: 'QUESTIONNAIRE',
    regulationCode: 'EU_AI_ACT',
    aiSystemId: ''
  },
  startAiSystemBodyResponse: null,
  showPreStep: false,
  preStepPart: 'basicInformation',
  disableNextStep: true,
  selectedEntity: null,
  nextQuestion: null,
  questionnaireId: '',
  aiSystemId: '',
  previousQuestions: [],
  providerDeclarationChecked: false,
  isStepBack: false,
  loading: false,
  isChangingAssessment: false,
  isFinalizingAIReport: false,
  answerRequestsInFlight: 0,
  creatingAiSystem: false,
  stepType: '',
  enableStepper: false,
  showEmptyState: false,
  isDraftLoaded: false,
  preStepInputError: null,
  serverErrorStatus: null
};

export function getCurrentStep(steps: Step[]) {
  return steps.findIndex((step) => step.status === 'current');
}

export function getCurrentStepId(steps: Step[]) {
  const current = steps.find((step) => step.status === 'current');
  return current ? current.id : '';
}

export function getPreviousStepId(steps: Step[]): string {
  const currentIndex = getCurrentStep(steps);
  const prevStep = steps[currentIndex - 1];
  return prevStep ? prevStep.id : '';
}

export const stepperSlice = createSlice({
  name: 'fileUpload',
  initialState,
  reducers: {
    stepNext: (state) => {
      const currentIndex = getCurrentStep(state.steps);

      if (currentIndex === state.steps.length - 1) return;

      state.steps[currentIndex].status = 'complete';
      state.steps[currentIndex + 1].status = 'current';

      state.disableNextStep = true;
      state.isStepBack = false;
      state.previousQuestions = [];
      state.serverErrorStatus = null;
    },
    stepBack: (state) => {
      const currentIndex = getCurrentStep(state.steps);

      if (currentIndex <= 0) return;

      state.steps[currentIndex].status = 'upcoming';
      state.steps[currentIndex - 1].status = 'current';

      state.disableNextStep = true;
      state.isStepBack = true;
      state.serverErrorStatus = null;
    },
    updateStepBasedOnBackend: (state, action: PayloadAction<string>) => {
      const backendStepId = action.payload;
      const currentIndex = getCurrentStep(state.steps);
      const nextIndex = state.steps.findIndex(
        (step) => step.id === backendStepId
      );

      // if we get same step
      if (nextIndex === currentIndex && !state.isStepBack) {
        state.disableNextStep = true;
        return;
      }

      // if we get next step from response enable next
      if (nextIndex > currentIndex) {
        state.disableNextStep = false;
      }

      // if we get back step or continue draft
      if (nextIndex < currentIndex || state.isDraftLoaded) {
        state.steps.forEach((step, index) => {
          if (index < nextIndex) step.status = 'complete';
          else if (index === nextIndex) step.status = 'current';
          else step.status = 'upcoming';
        });
        state.disableNextStep = true;
      }
    },
    resetStepper: () => {
      return initialState;
    },
    setShowPreStep: (state, action: PayloadAction<boolean>) => {
      state.showPreStep = action.payload;
    },
    setPreStepPart: (state, action: PayloadAction<PreStepPart>) => {
      state.preStepPart = action.payload;
    },
    setDisableNextStep: (state, action: PayloadAction<boolean>) => {
      state.disableNextStep = action.payload;
    },
    setSelectedEntity: (state, action: PayloadAction<Entity | null>) => {
      state.selectedEntity = action.payload;
    },
    setStartAiSystemBodyRequest: (
      state,
      action: PayloadAction<StartAiSystemRequest>
    ) => {
      state.startAiSystemBodyRequest = action.payload;
    },
    setStartAiSystemBodyResponse: (
      state,
      action: PayloadAction<StartAiSystemResponse | null>
    ) => {
      state.startAiSystemBodyResponse = action.payload;
      state.serverErrorStatus = null;

      if (action.payload) {
        // REGULATION_ASSESSMENT responses carry no questionnaireId — only set it
        // when present so it is not clobbered with undefined.
        if (action.payload.questionnaireId) {
          state.questionnaireId = action.payload.questionnaireId;
        }
        state.aiSystemId = action.payload.aiSystemId;
      }
    },
    setNextQuestion: (state, action: PayloadAction<NextQuestion | null>) => {
      state.nextQuestion = action.payload;
      state.serverErrorStatus = null;
    },
    setPreviousQuestions: (state, action: PayloadAction<Question[]>) => {
      state.previousQuestions = action.payload;
    },
    setProviderDeclarationChecked: (state, action: PayloadAction<boolean>) => {
      state.providerDeclarationChecked = action.payload;
    },
    setIsStepBack: (state, action: PayloadAction<boolean>) => {
      state.isStepBack = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsChangingAssessment: (state, action: PayloadAction<boolean>) => {
      state.isChangingAssessment = action.payload;
      if (action.payload) {
        state.serverErrorStatus = null;
      }
    },
    setIsFinalizingAIReport: (state, action: PayloadAction<boolean>) => {
      state.isFinalizingAIReport = action.payload;
    },
    incrementAnswerRequestsInFlight: (state) => {
      state.answerRequestsInFlight += 1;
    },
    decrementAnswerRequestsInFlight: (state) => {
      state.answerRequestsInFlight = Math.max(
        0,
        state.answerRequestsInFlight - 1
      );
    },
    setCreatingAiSystem: (state, action: PayloadAction<boolean>) => {
      state.creatingAiSystem = action.payload;
    },
    setStepType: (state, action: PayloadAction<string>) => {
      state.stepType = action.payload;
    },
    setEnableStepper: (state, action: PayloadAction<boolean>) => {
      state.enableStepper = action.payload;
    },
    setShowEmptyState: (state, action: PayloadAction<boolean>) => {
      state.showEmptyState = action.payload;
    },
    setIsDraftLoaded: (state, action: PayloadAction<boolean>) => {
      state.isDraftLoaded = action.payload;
    },
    setQuestionnaireId: (state, action: PayloadAction<string>) => {
      state.questionnaireId = action.payload;
    },
    setPreStepInputError: (state, action: PayloadAction<PreStepInputError>) => {
      state.preStepInputError = action.payload;
    },
    setAiSystemId: (state, action: PayloadAction<string>) => {
      state.aiSystemId = action.payload;
    },
    setQuestionnaireServerErrorStatus: (
      state,
      action: PayloadAction<number>
    ) => {
      state.serverErrorStatus = action.payload;
    },
    clearQuestionnaireServerError: (state) => {
      state.serverErrorStatus = null;
    }
  }
});

export const {
  stepNext,
  stepBack,
  setShowPreStep,
  setPreStepPart,
  setDisableNextStep,
  setSelectedEntity,
  setStartAiSystemBodyRequest,
  setStartAiSystemBodyResponse,
  setNextQuestion,
  setPreviousQuestions,
  updateStepBasedOnBackend,
  setProviderDeclarationChecked,
  setIsStepBack,
  setIsLoading,
  setIsChangingAssessment,
  setIsFinalizingAIReport,
  incrementAnswerRequestsInFlight,
  decrementAnswerRequestsInFlight,
  setCreatingAiSystem,
  setStepType,
  resetStepper,
  setEnableStepper,
  setShowEmptyState,
  setIsDraftLoaded,
  setQuestionnaireId,
  setPreStepInputError,
  setAiSystemId,
  setQuestionnaireServerErrorStatus,
  clearQuestionnaireServerError
} = stepperSlice.actions;

export default stepperSlice.reducer;
